// Master Apps — badges + avatar + captura da aplicação (porte de data.jsx).
import { useState } from 'react';
import {
  APP_TYPES,
  type App,
  STACK_COLORS,
  STATUS_TYPES,
  screenshotVariant,
} from '@/lib/catalog-config';
import { fontSans, liftForDark, makePalette, rotuloCaixaAlta } from '@/lib/theme';

/**
 * URL da imagem cadastrada, servida pela própria API a partir do bucket.
 * Null quando a aplicação não tem imagem (o card cai no visual sintético).
 *
 * Usamos `<img src>` direto: a rota exige sessão e, sendo same-origin, o cookie
 * do Clerk viaja junto. Se por qualquer motivo a requisição falhar (401, objeto
 * ausente, storage fora), o `onError` volta para o placeholder em vez de deixar
 * imagem quebrada na tela.
 */
export function applicationImageUrl(app: App, largura?: ImagemLargura): string | null {
  if (!app.imageKey) return null;
  return imageUrlFromKey(app.imageKey, largura);
}

/**
 * Larguras das variantes de thumb que a API publica (`?w=`). O mesmo trio de
 * `APPLICATION_IMAGE_VARIANT_WIDTHS` no shared: 320 para avatar/miniatura,
 * 640 para o hero do card, 1280 para deck e visualizador. Sem largura vem o
 * original — só quem precisa da imagem inteira (download, impressão) pede.
 */
export type ImagemLargura = 320 | 640 | 1280;

/**
 * URL de leitura a partir da CHAVE, sem passar pela aplicação inteira. A
 * galeria precisa disto: as telas adicionais são chaves soltas em
 * `app.galeriaKeys`, e montar um `App` falso só para reaproveitar
 * `applicationImageUrl` seria contorcionismo.
 */
export function imageUrlFromKey(key: string, largura?: ImagemLargura): string {
  const base = import.meta.env.VITE_API_URL || '';
  const url = `${base}/api/uploads/application-image/${encodeURIComponent(key)}`;
  return largura ? `${url}?w=${largura}` : url;
}

/**
 * Sequência COMPLETA de imagens da aplicação, na ordem de apresentação: a capa
 * primeiro, depois as telas adicionais.
 *
 * A capa mora em `imageKey` e as demais em `galeriaKeys` — quem consome quer a
 * lista única, e espalhar essa concatenação por carrossel, ficha e impressão
 * garantiria que um dia uma delas esqueceria a capa. Aplicação sem imagem
 * nenhuma devolve lista vazia (não `[null]`), para o chamador decidir entre
 * placeholder e nada.
 */
export function applicationImageUrls(app: App, largura?: ImagemLargura): string[] {
  const chaves = [app.imageKey, ...(app.galeriaKeys ?? [])].filter(
    (k): k is string => typeof k === 'string' && k !== '',
  );
  return chaves.map((k) => imageUrlFromKey(k, largura));
}

// lineHeight 1 nos badges com bolinha: com o line box do MESMO tamanho da
// fonte, o centro do box coincide com o centro óptico das maiúsculas em
// qualquer fonte (inclusive no fallback quando o Google Fonts não carrega) e a
// conta do flexbox sai em pixels inteiros — sem a bolinha "afundar" meio pixel
// em monitores com escala 125/150%.
export function AppTypeTag({ type, dark }: Readonly<{ type: string; dark: boolean }>) {
  const t = APP_TYPES[type] ?? {
    label: type,
    color: '#475569',
    bg: '#f1f5f9',
    dark: '#47556922',
  };
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '3px 8px',
        borderRadius: 4,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.05em',
        textTransform: 'uppercase',
        background: dark ? t.dark : t.bg,
        color: t.color,
        lineHeight: 1,
      }}
    >
      <span
        style={{
          width: 5,
          height: 5,
          borderRadius: '50%',
          background: t.color,
          // −0.5px óptico: o hinting ancora a baseline do texto em pixel
          // inteiro e "sobe" as maiúsculas ~meio pixel; a bolinha acompanha.
          transform: 'translateY(-0.5px)',
        }}
      />
      {t.label}
    </span>
  );
}

export function StackChip({ name, dark }: Readonly<{ name: string; dark: boolean }>) {
  const p = makePalette(dark);
  const color = STACK_COLORS[name] ?? '#475569';
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '3px 8px 3px 6px',
        borderRadius: 4,
        fontSize: 11,
        fontWeight: 500,
        background: p.surface2,
        color: p.text,
        border: `1px solid ${p.border}`,
        fontFamily: fontSans,
        whiteSpace: 'nowrap',
        lineHeight: 1.2,
      }}
    >
      <span style={{ width: 7, height: 7, borderRadius: 2, background: color }} />
      {name}
    </span>
  );
}

/**
 * Pintura do círculo do selo de visibilidade. Fora do JSX de propósito: como
 * ternário aninhado no `style` a regra ficava ilegível, e aqui os três casos
 * aparecem lado a lado.
 */
function visibilityFillBackground(fill: 'none' | 'half' | 'full', color: string): string {
  if (fill === 'full') return color;
  if (fill === 'half') return `linear-gradient(90deg, ${color} 50%, transparent 50%)`;
  return 'transparent';
}

export function VisibilityBadge({ visibility }: Readonly<{ visibility: string; dark: boolean }>) {
  const config = (
    {
      // fill: quanto do círculo é preenchido — vazado (público), metade
      // (privado), cheio (interno). Era um caractere de texto (○ ◐ ●), mas
      // glifo senta na BASELINE da fonte e ficava visivelmente abaixo do
      // centro do texto; como forma CSS, o flexbox centraliza de verdade.
      publico: { label: 'Público', color: '#16a34a', fill: 'none' },
      privado: { label: 'Privado', color: '#ca8a04', fill: 'half' },
      interno: { label: 'Interno', color: '#dc2626', fill: 'full' },
    } as Record<string, { label: string; color: string; fill: 'none' | 'half' | 'full' }>
  )[visibility] ?? { label: visibility, color: '#78716c', fill: 'full' };
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: config.color,
        lineHeight: 1,
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          boxSizing: 'border-box',
          border: `1.5px solid ${config.color}`,
          background: visibilityFillBackground(config.fill, config.color),
          flexShrink: 0,
          // −0.5px óptico (ver comentário do AppTypeTag).
          transform: 'translateY(-0.5px)',
        }}
      />
      {config.label}
    </span>
  );
}

export function CriticalityBadge({ level }: Readonly<{ level: string; dark: boolean }>) {
  const config = (
    {
      alta: { label: 'Crítica', color: '#dc2626' },
      media: { label: 'Média', color: '#ca8a04' },
      baixa: { label: 'Baixa', color: '#16a34a' },
    } as Record<string, { label: string; color: string }>
  )[level] ?? { label: level, color: '#78716c' };
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        color: config.color,
        lineHeight: 1,
      }}
    >
      <span
        style={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          background: config.color,
          // −0.5px óptico (ver comentário do AppTypeTag).
          transform: 'translateY(-0.5px)',
        }}
      />
      {config.label}
    </span>
  );
}

export function StatusBadge({ status, dark }: Readonly<{ status: string; dark: boolean }>) {
  const s = STATUS_TYPES[status] ?? STATUS_TYPES.prod;
  return (
    <span
      style={{
        ...rotuloCaixaAlta,
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        padding: '2px 7px',
        borderRadius: 3,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        background: dark ? `${s.color}22` : s.bg,
        color: s.color,
      }}
    >
      {s.label}
    </span>
  );
}

// Avatar pequeno — usado em tabelas, header do detalhe, admin.
export function AppPhoto({
  app,
  size = 56,
  dark,
}: Readonly<{ app: App; size?: number; dark: boolean }>) {
  const iconBg = liftForDark(app.iconBg, dark);
  const pid = `p-${app.slug}-${size}`;
  // Avatar pequeno: a variante de 320 basta até em tela retina.
  const imageUrl = applicationImageUrl(app, 320);
  // Guarda a URL que falhou (não um boolean): trocar a imagem no cadastro muda
  // a URL e o avatar volta a tentar — um boolean "grudava" no placeholder até
  // o remount, mesmo depois de o admin corrigir a imagem.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const imageFailed = imageUrl !== null && failedUrl === imageUrl;

  if (imageUrl && !imageFailed) {
    return (
      <img
        src={imageUrl}
        alt={app.name}
        width={size}
        height={size}
        loading="lazy"
        decoding="async"
        onError={() => setFailedUrl(imageUrl)}
        style={{
          width: size,
          height: size,
          borderRadius: size * 0.22,
          objectFit: 'cover',
          flexShrink: 0,
          display: 'block',
          background: iconBg,
        }}
      />
    );
  }

  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.22,
        background: `linear-gradient(135deg, ${iconBg} 0%, ${iconBg}cc 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#fff',
        fontWeight: 700,
        fontSize: size * 0.42,
        letterSpacing: '-0.02em',
        fontFamily: fontSans,
        flexShrink: 0,
        boxShadow: `0 4px 12px -2px ${iconBg}44`,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      <svg style={{ position: 'absolute', inset: 0, opacity: 0.18 }} width="100%" height="100%">
        <defs>
          <pattern id={pid} width="8" height="8" patternUnits="userSpaceOnUse">
            <path d="M 8 0 L 0 0 0 8" fill="none" stroke="#fff" strokeWidth="0.5" />
          </pattern>
        </defs>
        <rect width="100%" height="100%" fill={`url(#${pid})`} />
      </svg>
      <span style={{ position: 'relative', zIndex: 1 }}>{app.iconLetter}</span>
    </div>
  );
}

// Screenshot placeholder — hero faux-UI estilizado que sugere "preview do app".
export function AppScreenshot({
  app,
  dark,
  height = 140,
  env = 'prod',
}: Readonly<{
  app: App;
  dark: boolean;
  height?: number;
  env?: 'prod' | 'homolog';
}>) {
  const variant = screenshotVariant(app.slug);
  const accent = app.iconBg;
  const isHomolog = env === 'homolog';
  // Hero do card (≈300–400 px de largura): 640 cobre retina sem baixar o original.
  const imageUrl = applicationImageUrl(app, 640);
  // Guarda a URL que falhou (não um boolean): trocar a imagem no cadastro muda
  // a URL e o card volta a tentar — um boolean "grudava" no sintético até o
  // remount, mesmo depois de o admin corrigir a imagem.
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  // Captura a exibir, ou null quando não há imagem cadastrada ou o carregamento
  // falhou — nesse caso o card cai no visual sintético. Uma variável só (em vez
  // do par `showImage` + recheque de `imageUrl` no JSX) já estreita o tipo.
  const shownImage = imageUrl !== null && failedUrl === imageUrl ? null : imageUrl;
  const bgs = [
    `linear-gradient(135deg, ${accent} 0%, ${accent}dd 60%, ${accent}99 100%)`,
    `linear-gradient(145deg, ${accent}cc 0%, ${accent} 100%)`,
    `linear-gradient(155deg, #0a0a0b 0%, ${accent}cc 80%, ${accent} 100%)`,
    `linear-gradient(125deg, ${accent} 0%, ${accent}aa 100%)`,
  ];

  const renderMock = () => {
    if (variant === 0) {
      return (
        <g>
          <rect x="22" y="22" width="86" height="10" rx="2" fill="#ffffff" opacity="0.92" />
          <rect x="22" y="38" width="56" height="5" rx="2" fill="#ffffff" opacity="0.55" />
          {[0, 1, 2, 3].map((i) => (
            <rect
              key={i}
              x={22 + i * 44}
              y="60"
              width="36"
              height="38"
              rx="4"
              fill="#ffffff"
              opacity={0.18 + i * 0.05}
            />
          ))}
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <rect
              key={i}
              x={22 + i * 22}
              y={120 - (10 + ((i * 7) % 26))}
              width="14"
              height={10 + ((i * 7) % 26)}
              rx="2"
              fill="#ffffff"
              opacity="0.85"
            />
          ))}
        </g>
      );
    }
    if (variant === 1) {
      return (
        <g>
          <rect x="22" y="22" width="66" height="9" rx="2" fill="#ffffff" opacity="0.92" />
          <rect x="160" y="22" width="32" height="9" rx="2" fill="#ffffff" opacity="0.55" />
          {[0, 1, 2, 3, 4].map((i) => (
            <g key={i}>
              <rect
                x="22"
                y={48 + i * 16}
                width="170"
                height="12"
                rx="3"
                fill="#ffffff"
                opacity={i % 2 === 0 ? 0.12 : 0.2}
              />
              <circle cx="32" cy={54 + i * 16} r="3" fill="#ffffff" opacity="0.9" />
              <rect
                x="42"
                y={51 + i * 16}
                width="60"
                height="6"
                rx="1.5"
                fill="#ffffff"
                opacity="0.85"
              />
              <rect
                x="110"
                y={51 + i * 16}
                width="30"
                height="6"
                rx="1.5"
                fill="#ffffff"
                opacity="0.6"
              />
            </g>
          ))}
        </g>
      );
    }
    if (variant === 2) {
      return (
        <g>
          <rect x="22" y="22" width="90" height="10" rx="2" fill="#ffffff" opacity="0.92" />
          {[0, 1, 2].map((i) => (
            <g key={i}>
              <rect
                x="22"
                y={48 + i * 22}
                width="40"
                height="5"
                rx="1"
                fill="#ffffff"
                opacity="0.55"
              />
              <rect
                x="22"
                y={56 + i * 22}
                width="170"
                height="12"
                rx="3"
                fill="#ffffff"
                opacity="0.18"
              />
            </g>
          ))}
          <rect x="22" y="118" width="60" height="14" rx="3" fill="#ffffff" opacity="0.92" />
        </g>
      );
    }
    return (
      <g>
        <rect x="22" y="22" width="56" height="9" rx="2" fill="#ffffff" opacity="0.92" />
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <rect
            key={i}
            x={22 + (i % 3) * 60}
            y={46 + Math.floor(i / 3) * 44}
            width="50"
            height="36"
            rx="4"
            fill="#ffffff"
            opacity={0.14 + (i % 3) * 0.08}
          />
        ))}
      </g>
    );
  };

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height,
        borderRadius: '10px 10px 0 0',
        background: bgs[variant],
        overflow: 'hidden',
      }}
    >
      {shownImage ? (
        // Captura real cadastrada no formulário. Se falhar, cai no sintético.
        <img
          src={shownImage}
          alt={`Captura de tela de ${app.name}`}
          loading="lazy"
          decoding="async"
          onError={() => setFailedUrl(shownImage)}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            objectFit: 'cover',
            // Âncora no TOPO: o hero do card é mais largo que 16:9, então o
            // cover corta na vertical — centralizado, sobrava só a faixa do
            // meio da captura. Screenshot de app se reconhece pelo cabeçalho:
            // corta-se só a parte de baixo.
            objectPosition: 'top',
            display: 'block',
          }}
        />
      ) : (
        <>
          <svg style={{ position: 'absolute', inset: 0, opacity: 0.14 }} width="100%" height="100%">
            <defs>
              <pattern id={`scr-${app.slug}`} width="22" height="22" patternUnits="userSpaceOnUse">
                <path d="M 22 0 L 0 0 0 22" fill="none" stroke="#fff" strokeWidth="0.5" />
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill={`url(#scr-${app.slug})`} />
          </svg>
          <svg
            style={{ position: 'absolute', inset: 0 }}
            viewBox={`0 0 220 ${height}`}
            preserveAspectRatio="xMidYMid slice"
            width="100%"
            height="100%"
          >
            {renderMock()}
          </svg>
        </>
      )}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: 50,
          background: `linear-gradient(180deg, transparent, ${dark ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.25)'})`,
        }}
      />
      {isHomolog && (
        <>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(202,138,4,0.18)',
              mixBlendMode: 'multiply',
            }}
          />
          <div
            style={{
              ...rotuloCaixaAlta,
              position: 'absolute',
              bottom: 10,
              left: 10,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '4px 9px',
              borderRadius: 4,
              fontSize: 10,
              fontWeight: 700,
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              background: '#ca8a04',
              color: '#fff',
              boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
            }}
          >
            <span style={{ width: 5, height: 5, borderRadius: '50%', background: '#fff' }} />
            {'Homologação'}
          </div>
        </>
      )}
    </div>
  );
}
